import type { JointKey } from './JointAngles';
import type { PoseRecord } from './PoseRecordStore';

/**
 * Compact binary format for recorded pose data.
 *
 * Goals: ~50-80× smaller than JSON, zero-copy typed-array access in the
 * viewer, zero precision loss relative to the pose model's own noise floor.
 *
 * File layout: gzip(payload). The gzip magic bytes (0x1F 0x8B) double as the
 * file-type signature — anything else is assumed to be the legacy JSON export.
 *
 * Inside the compressed payload (all little-endian):
 *
 *   [0..4)    magic "BNLP"
 *   [4..6)    version (uint16) -- bump when field layout changes
 *   [6..8)    flags   (uint16) -- reserved, 0 for v1
 *   [8..16)   recordingStartedAt (float64, ms)
 *   [16..18)  resolutionWidth    (uint16)
 *   [18..20)  resolutionHeight   (uint16)
 *   [20..21)  numNodes           (uint8)
 *   [21..25)  numFrames          (uint32)
 *   [25..26)  numAngleKeys       (uint8)
 *   ...       4 UTF-8 strings (len:u8 + bytes): engineId, engineLabel,
 *                                               source, (numAngleKeys) × angleKey names
 *   Data arrays (tightly packed, frame-major):
 *     timestamps: float32 × numFrames           (ms relative to recordingStartedAt)
 *     x:          uint16  × numFrames × numNodes (val / 65535 ∈ [0,1])
 *     y:          uint16  × numFrames × numNodes
 *     score:      uint8   × numFrames × numNodes (val / 255)
 *     angles:     int16   × numFrames × numAngleKeys (val / 100°, -32768 = NaN)
 */

const MAGIC = [0x42, 0x4e, 0x4c, 0x50] as const; // "BNLP"
const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;
export const POSE_BINARY_VERSION_V1 = 1;
export const POSE_BINARY_VERSION_V2 = 2;

const X_SCALE = 65535;
const Y_SCALE = 65535;
const SCORE_SCALE = 255;
const ANGLE_SCALE = 100;
const ANGLE_NAN_SENTINEL = -32768;

// World coordinates: int16 with 1mm resolution → range ±32.767 m. Hip-centered
// MediaPipe world landmarks stay inside ±1.5 m in practice, so 32m is gratuitously
// safe.
const WORLD_SCALE = 1000; // m -> mm
const WORLD_NAN_SENTINEL = -32768;
/** Flag bit: world-space landmarks are present and stored after the 2D data. */
const FLAG_HAS_WORLD = 0x01;

export interface PoseBinaryMeta {
  engineId?: string;
  engineLabel?: string;
  source?: string;
  resolution?: { width: number; height: number };
  recordingStartedAt?: number;
  angleKeys: readonly JointKey[];
}

export interface DecodedPoseBinary {
  version: number;
  engineId?: string;
  engineLabel?: string;
  source?: string;
  resolution?: { width: number; height: number };
  recordingStartedAt: number;
  numFrames: number;
  numNodes: number;
  angleKeys: JointKey[];
  /** ms from recordingStartedAt */
  timestamps: Float32Array;
  /** [numFrames × numNodes], normalized [0,1] */
  x: Float32Array;
  y: Float32Array;
  /** [numFrames × numNodes], [0,1] */
  score: Float32Array;
  /** [numFrames × numAngleKeys], degrees, NaN for missing */
  angles: Float32Array;
  /** True when `worldX/Y/Z/worldVisibility` are populated. */
  hasWorld: boolean;
  /** [numFrames × numNodes], meters (hip-centered). NaN when landmark is absent. */
  worldX?: Float32Array;
  worldY?: Float32Array;
  worldZ?: Float32Array;
  /** [numFrames × numNodes], [0,1]. */
  worldVisibility?: Float32Array;
}

/**
 * Returns true if the given buffer looks like a gzipped pose binary (any
 * version). Use before trying to decode.
 */
export function isPoseBinary(bin: Uint8Array): boolean {
  return bin.length >= 2 && bin[0] === GZIP_MAGIC_0 && bin[1] === GZIP_MAGIC_1;
}

/**
 * Encode pose records + metadata into a gzipped binary Uint8Array.
 *
 * Picks v2 when any frame carries world-space landmarks (MediaPipe), else v1.
 * Only the primary pose per frame (`record.poses[0]`) is stored; multi-person
 * support is reserved for a future version.
 */
export async function encodePoseBinary(
  records: readonly PoseRecord[],
  meta: PoseBinaryMeta,
): Promise<Uint8Array> {
  const numFrames = records.length;
  const firstPose = records.find((r) => r.poses.length > 0)?.poses[0];
  const numNodes = firstPose?.nodes.length ?? 0;
  const angleKeys = meta.angleKeys;
  const numAngleKeys = angleKeys.length;
  const recordingStartedAt = meta.recordingStartedAt ?? records[0]?.t ?? 0;

  const hasWorld = records.some((r) => r.poses[0]?.nodes.some((n) => typeof n.worldX === 'number'));
  const version = hasWorld ? POSE_BINARY_VERSION_V2 : POSE_BINARY_VERSION_V1;
  const flags = hasWorld ? FLAG_HAS_WORLD : 0;

  const engineIdBytes = utf8(meta.engineId ?? '');
  const engineLabelBytes = utf8(meta.engineLabel ?? '');
  const sourceBytes = utf8(meta.source ?? '');
  const angleKeyBytes = angleKeys.map((k) => utf8(k));

  // We build the header and the binary data sections in separate buffers
  // and concat them at the end: the string lengths are variable so data
  // offsets can't be guaranteed 4-byte aligned inside a single buffer, and
  // typed-array views require alignment.
  const headerSize =
    4 + // magic
    2 + // version
    2 + // flags
    8 + // recordingStartedAt
    2 + // resW
    2 + // resH
    1 + // numNodes
    4 + // numFrames
    1 + // numAngleKeys
    1 +
    engineIdBytes.length +
    1 +
    engineLabelBytes.length +
    1 +
    sourceBytes.length +
    angleKeyBytes.reduce((acc, b) => acc + 1 + b.length, 0);

  const scoreBytes = numFrames * numNodes;
  // `angles` / `worldX` / ... are Int16 → their offsets must be 2-aligned.
  // `score` / `worldVisibility` (uint8) can leave odd offsets, so pad to even.
  const scorePad = scoreBytes & 1;
  const worldNodes = numFrames * numNodes;
  const worldVisBytes = hasWorld ? worldNodes : 0;
  const worldVisPad = worldVisBytes & 1;

  const dataBytes =
    4 * numFrames +
    2 * worldNodes + // x
    2 * worldNodes + // y
    scoreBytes +
    scorePad +
    2 * numFrames * numAngleKeys + // angles
    (hasWorld
      ? 2 * worldNodes + // worldX
        2 * worldNodes + // worldY
        2 * worldNodes + // worldZ
        worldVisBytes +
        worldVisPad
      : 0);
  const dataBuf = new ArrayBuffer(dataBytes);

  const headerBuf = new ArrayBuffer(headerSize);
  const dv = new DataView(headerBuf);
  const headerU8 = new Uint8Array(headerBuf);

  let off = 0;
  for (const b of MAGIC) headerU8[off++] = b;
  dv.setUint16(off, version, true);
  off += 2;
  dv.setUint16(off, flags, true);
  off += 2;
  dv.setFloat64(off, recordingStartedAt, true);
  off += 8;
  dv.setUint16(off, meta.resolution?.width ?? 0, true);
  off += 2;
  dv.setUint16(off, meta.resolution?.height ?? 0, true);
  off += 2;
  dv.setUint8(off, numNodes);
  off += 1;
  dv.setUint32(off, numFrames, true);
  off += 4;
  dv.setUint8(off, numAngleKeys);
  off += 1;

  off = writeString(headerU8, off, engineIdBytes);
  off = writeString(headerU8, off, engineLabelBytes);
  off = writeString(headerU8, off, sourceBytes);
  for (const b of angleKeyBytes) off = writeString(headerU8, off, b);

  let dOff = 0;
  const tsView = new Float32Array(dataBuf, dOff, numFrames);
  dOff += 4 * numFrames;
  const xView = new Uint16Array(dataBuf, dOff, worldNodes);
  dOff += 2 * worldNodes;
  const yView = new Uint16Array(dataBuf, dOff, worldNodes);
  dOff += 2 * worldNodes;
  const scoreView = new Uint8Array(dataBuf, dOff, scoreBytes);
  dOff += scoreBytes + scorePad;
  const anglesView = new Int16Array(dataBuf, dOff, numFrames * numAngleKeys);
  dOff += 2 * numFrames * numAngleKeys;

  let wxView: Int16Array | null = null;
  let wyView: Int16Array | null = null;
  let wzView: Int16Array | null = null;
  let wvView: Uint8Array | null = null;
  if (hasWorld) {
    wxView = new Int16Array(dataBuf, dOff, worldNodes);
    dOff += 2 * worldNodes;
    wyView = new Int16Array(dataBuf, dOff, worldNodes);
    dOff += 2 * worldNodes;
    wzView = new Int16Array(dataBuf, dOff, worldNodes);
    dOff += 2 * worldNodes;
    wvView = new Uint8Array(dataBuf, dOff, worldVisBytes);
    dOff += worldVisBytes + worldVisPad;
  }

  for (let fi = 0; fi < numFrames; fi += 1) {
    const rec = records[fi];
    tsView[fi] = (rec.t - recordingStartedAt) as number;
    const pose = rec.poses[0];
    const nodeOff = fi * numNodes;
    if (pose) {
      for (let ni = 0; ni < numNodes; ni += 1) {
        const n = pose.nodes[ni];
        if (n) {
          xView[nodeOff + ni] = clampQuantize(n.x, X_SCALE);
          yView[nodeOff + ni] = clampQuantize(n.y, Y_SCALE);
          scoreView[nodeOff + ni] = clampQuantize(n.score, SCORE_SCALE);
          if (hasWorld && wxView && wyView && wzView && wvView) {
            wxView[nodeOff + ni] = quantizeWorld(n.worldX);
            wyView[nodeOff + ni] = quantizeWorld(n.worldY);
            wzView[nodeOff + ni] = quantizeWorld(n.worldZ);
            wvView[nodeOff + ni] = clampQuantize(n.worldVisibility ?? 0, SCORE_SCALE);
          }
        } else if (hasWorld && wxView && wyView && wzView) {
          wxView[nodeOff + ni] = WORLD_NAN_SENTINEL;
          wyView[nodeOff + ni] = WORLD_NAN_SENTINEL;
          wzView[nodeOff + ni] = WORLD_NAN_SENTINEL;
        }
      }
    } else if (hasWorld && wxView && wyView && wzView) {
      for (let ni = 0; ni < numNodes; ni += 1) {
        wxView[nodeOff + ni] = WORLD_NAN_SENTINEL;
        wyView[nodeOff + ni] = WORLD_NAN_SENTINEL;
        wzView[nodeOff + ni] = WORLD_NAN_SENTINEL;
      }
    }
    const angleOff = fi * numAngleKeys;
    const a = rec.angles;
    for (let ki = 0; ki < numAngleKeys; ki += 1) {
      anglesView[angleOff + ki] = quantizeAngle(a?.[angleKeys[ki]]);
    }
  }

  const combined = new Uint8Array(headerBuf.byteLength + dataBuf.byteLength);
  combined.set(new Uint8Array(headerBuf), 0);
  combined.set(new Uint8Array(dataBuf), headerBuf.byteLength);
  return gzip(combined);
}

/**
 * Decode a gzipped v1/v2 pose binary back into typed arrays ready for the viewer.
 */
export async function decodePoseBinary(data: Uint8Array): Promise<DecodedPoseBinary> {
  const payload = await gunzip(data);
  const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);

  let off = 0;
  for (let i = 0; i < MAGIC.length; i += 1) {
    if (payload[off++] !== MAGIC[i]) {
      throw new Error('PoseBinaryFormat: magic mismatch (not a BNLP file)');
    }
  }
  const version = dv.getUint16(off, true);
  off += 2;
  if (version !== POSE_BINARY_VERSION_V1 && version !== POSE_BINARY_VERSION_V2) {
    throw new Error(`PoseBinaryFormat: unsupported version ${version}`);
  }
  const flags = dv.getUint16(off, true);
  off += 2;
  const hasWorld = version >= POSE_BINARY_VERSION_V2 && (flags & FLAG_HAS_WORLD) !== 0;
  const recordingStartedAt = dv.getFloat64(off, true);
  off += 8;
  const resW = dv.getUint16(off, true);
  off += 2;
  const resH = dv.getUint16(off, true);
  off += 2;
  const numNodes = dv.getUint8(off);
  off += 1;
  const numFrames = dv.getUint32(off, true);
  off += 4;
  const numAngleKeys = dv.getUint8(off);
  off += 1;

  const engineId = readString(payload, off);
  off = engineId.next;
  const engineLabel = readString(payload, off);
  off = engineLabel.next;
  const source = readString(payload, off);
  off = source.next;
  const angleKeys: JointKey[] = [];
  for (let i = 0; i < numAngleKeys; i += 1) {
    const s = readString(payload, off);
    angleKeys.push(s.value as JointKey);
    off = s.next;
  }

  // NB: typed-array views require aligned byte offsets inside the buffer;
  // since we read length-prefixed strings above, `off` may be odd. Copy the
  // remaining bytes into a fresh ArrayBuffer to guarantee 4-byte alignment.
  const tailLen = payload.byteLength - off;
  const tail = new ArrayBuffer(tailLen);
  new Uint8Array(tail).set(payload.subarray(off));
  let tOff = 0;

  const totalNodes = numFrames * numNodes;
  const scoreBytes = totalNodes;
  const scorePad = scoreBytes & 1;

  const timestamps = new Float32Array(tail, tOff, numFrames);
  tOff += 4 * numFrames;
  const xRaw = new Uint16Array(tail, tOff, totalNodes);
  tOff += 2 * totalNodes;
  const yRaw = new Uint16Array(tail, tOff, totalNodes);
  tOff += 2 * totalNodes;
  const scoreRaw = new Uint8Array(tail, tOff, scoreBytes);
  tOff += scoreBytes + scorePad;
  const anglesRaw = new Int16Array(tail, tOff, numFrames * numAngleKeys);
  tOff += 2 * numFrames * numAngleKeys;

  let wxRaw: Int16Array | null = null;
  let wyRaw: Int16Array | null = null;
  let wzRaw: Int16Array | null = null;
  let wvRaw: Uint8Array | null = null;
  if (hasWorld) {
    const worldVisBytes = totalNodes;
    const worldVisPad = worldVisBytes & 1;
    wxRaw = new Int16Array(tail, tOff, totalNodes);
    tOff += 2 * totalNodes;
    wyRaw = new Int16Array(tail, tOff, totalNodes);
    tOff += 2 * totalNodes;
    wzRaw = new Int16Array(tail, tOff, totalNodes);
    tOff += 2 * totalNodes;
    wvRaw = new Uint8Array(tail, tOff, worldVisBytes);
    tOff += worldVisBytes + worldVisPad;
  }

  const x = new Float32Array(xRaw.length);
  const y = new Float32Array(yRaw.length);
  const score = new Float32Array(scoreRaw.length);
  const angles = new Float32Array(anglesRaw.length);
  for (let i = 0; i < xRaw.length; i += 1) {
    x[i] = xRaw[i] / X_SCALE;
    y[i] = yRaw[i] / Y_SCALE;
    score[i] = scoreRaw[i] / SCORE_SCALE;
  }
  for (let i = 0; i < anglesRaw.length; i += 1) {
    angles[i] = anglesRaw[i] === ANGLE_NAN_SENTINEL ? Number.NaN : anglesRaw[i] / ANGLE_SCALE;
  }

  let worldX: Float32Array | undefined;
  let worldY: Float32Array | undefined;
  let worldZ: Float32Array | undefined;
  let worldVisibility: Float32Array | undefined;
  if (hasWorld && wxRaw && wyRaw && wzRaw && wvRaw) {
    worldX = new Float32Array(totalNodes);
    worldY = new Float32Array(totalNodes);
    worldZ = new Float32Array(totalNodes);
    worldVisibility = new Float32Array(totalNodes);
    for (let i = 0; i < totalNodes; i += 1) {
      worldX[i] = wxRaw[i] === WORLD_NAN_SENTINEL ? Number.NaN : wxRaw[i] / WORLD_SCALE;
      worldY[i] = wyRaw[i] === WORLD_NAN_SENTINEL ? Number.NaN : wyRaw[i] / WORLD_SCALE;
      worldZ[i] = wzRaw[i] === WORLD_NAN_SENTINEL ? Number.NaN : wzRaw[i] / WORLD_SCALE;
      worldVisibility[i] = wvRaw[i] / SCORE_SCALE;
    }
  }

  return {
    version,
    engineId: engineId.value || undefined,
    engineLabel: engineLabel.value || undefined,
    source: source.value || undefined,
    resolution: resW > 0 && resH > 0 ? { width: resW, height: resH } : undefined,
    recordingStartedAt,
    numFrames,
    numNodes,
    angleKeys,
    timestamps: new Float32Array(timestamps), // copy out of tail buffer
    x,
    y,
    score,
    angles,
    hasWorld,
    worldX,
    worldY,
    worldZ,
    worldVisibility,
  };
}

function clampQuantize(v: number, scale: number): number {
  if (!Number.isFinite(v)) return 0;
  const c = v < 0 ? 0 : v > 1 ? 1 : v;
  return Math.round(c * scale);
}

function quantizeAngle(v: number | undefined): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return ANGLE_NAN_SENTINEL;
  const clamped = Math.max(-180, Math.min(327, v));
  return Math.round(clamped * ANGLE_SCALE);
}

function quantizeWorld(v: number | undefined): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return WORLD_NAN_SENTINEL;
  // int16 range excluding sentinel: -32767..32767 → ±32.767 m
  const mm = Math.round(v * WORLD_SCALE);
  if (mm <= WORLD_NAN_SENTINEL) return -32767;
  if (mm > 32767) return 32767;
  return mm;
}

function utf8(s: string): Uint8Array {
  const enc = new TextEncoder().encode(s);
  if (enc.length > 255) throw new Error(`PoseBinaryFormat: string too long (${enc.length} > 255)`);
  return enc;
}

function writeString(u8: Uint8Array, off: number, bytes: Uint8Array): number {
  u8[off] = bytes.length;
  u8.set(bytes, off + 1);
  return off + 1 + bytes.length;
}

function readString(u8: Uint8Array, off: number): { value: string; next: number } {
  const len = u8[off];
  const bytes = u8.subarray(off + 1, off + 1 + len);
  return { value: new TextDecoder().decode(bytes), next: off + 1 + len };
}

async function gzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function gunzip(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
